import type {
  ExecutionTemplate,
  CreateExecutionTemplateRequest,
  UpdateExecutionTemplateRequest,
  ExecutionTemplateType,
  ExecutionTemplateCategory,
  ExecutionPipeline,
  CreateExecutionPipelineRequest,
  UpdateExecutionPipelineRequest,
  ExecutionPipelineItem,
  CreateExecutionPipelineItemRequest,
  UpdateExecutionPipelineItemRequest,
  ExecutionPipelineRun,
  ExecutionPipelineRunItem,
  ExecutionSchedule,
} from '../types';

/**
 * Base configuration for API client
 */
interface ClientConfig {
  baseUrl: string;
  headers?: Record<string, string>;
}

/**
 * API Response wrapper - CORRECTED to match actual GoWebResponse structure
 */
interface GoWebResponse<T> {
  data?: T;
  metadata?: {
    requestId?: string;
    timestamp?: string;
    version?: string;
  };
  errors?: {
    errors?: Array<{ code: string; message: string }>;
  };
}

interface GoWebStandardListResponse {
  data?: any[];
  metadata?: {
    requestId?: string;
    timestamp?: string;
    version?: string;
  };
  errors?: {
    errors?: Array<{ code: string; message: string }>;
  };
}

interface GoWebStandardSearchResponse {
  data?: any[];
  pageInfo?: {
    pageNumber: number;
    pageSize: number;
    totalRecords: number;
    totalPages: number;
  };
  metadata?: {
    requestId?: string;
    timestamp?: string;
    version?: string;
  };
  errors?: {
    errors?: Array<{ code: string; message: string }>;
  };
}

interface InsertWebResponse {
  success?: boolean;
  data?: any;
  message?: string;
  errors?: { errors?: Array<{ code: string; message: string }> };
}

interface UpdateWebResponse {
  success?: boolean;
  data?: any;
  message?: string;
  errors?: { errors?: Array<{ code: string; message: string }> };
}

interface DeleteWebResponse {
  success?: boolean;
  message?: string;
  errors?: { errors?: Array<{ code: string; message: string }> };
}

/**
 * Request wrappers that match backend GoWebRequest structures
 */
interface InsertWebRequest<T = any> {
  data?: T;
  id?: { value?: number | string };
}

interface UpdateWebRequest<T = any> {
  data?: T;
  executionPipelineID?: number;
  executionTemplateID?: number;
  executionPipelineItemID?: number;
  id?: { value?: number | string };
}

interface DeleteWebRequest {
  id?: { value?: number | string };
  ID?: number;
}

interface GetByIdRequest {
  id?: { value?: number | string };
  ID?: number;
}

/**
 * Generic API Client for Execution Templates, Pipelines, etc.
 * Calls Phase 2/3 endpoints in BizFirst.Ai.Process.Api.Base
 * CRUD endpoints: /api/v1/process/*
 * Execution endpoints: /api/v1/process-engine/execution/*
 */
export class ExecutionPipelinesApiClient {
  private baseUrl: string;
  private staticHeaders: Record<string, string>;

  constructor(config: ClientConfig) {
    this.baseUrl = config.baseUrl;
    this.staticHeaders = {
      'Content-Type': 'application/json',
      ...config.headers,
    };
  }

  /**
   * Headers rebuilt on every call (not cached at construction) -- every constructor call site in
   * this app builds one of these per-modal-open without ever passing `config.headers`, so a
   * headers snapshot taken once at construction time never carried an Authorization token in the
   * first place. Real, reproduced bug: every `execution-template*`/`execution-templates/*` call
   * this client makes returned 401 (Create Template's Type/Category dropdowns and the Save button
   * itself), while every other API call in this app succeeded -- those go through
   * `BaseApiClient.getHeaders()` (`flow-studio-api/src/clients/baseApiClient.ts`), which already
   * reads `localStorage.getItem('authToken')` fresh on every request. Matching that same
   * convention here (not a new one) fixes every caller of this client at once, including
   * `flow-pipelines`'s `AppShell.tsx`, whose own comment already flagged "auth token propagation
   * into this client is a separate, later piece of work" as a known gap.
   */
  private getHeaders(): Record<string, string> {
    const headers: Record<string, string> = { ...this.staticHeaders };
    const token = localStorage.getItem('authToken');
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    return headers;
  }

  /**
   * Generic GET request
   */
  private async get<T>(path: string): Promise<T> {
    const response = await fetch(`${this.baseUrl}${path}`, {
      method: 'GET',
      headers: this.getHeaders(),
    });

    if (!response.ok) {
      throw new Error(`API Error: ${response.statusText}`);
    }

    const data = await response.json();
    return data.data || data;
  }

  /**
   * Extracts a human-readable message off a failed response's body, so a caller-facing error (e.g.
   * a toast) shows the server's actual reason instead of a generic HTTP status phrase. Reads the
   * body defensively across this API's several failure shapes -- an ASP.NET ProblemDetails payload
   * (`detail`/`title`, what BaseRepository's InvalidOperationException-driven 400s return, e.g. the
   * DefaultTriggerMode partial-update deserialization failure this was written to surface), and a
   * GoWebResponse-shaped payload (`errors[].message`/`message`). Never throws -- a body that isn't
   * JSON, or has neither shape, falls back to the HTTP status text.
   */
  private async extractErrorMessage(response: Response): Promise<string> {
    try {
      const body = await response.clone().json();
      if (typeof body?.detail === 'string' && body.detail) return body.detail;
      if (typeof body?.title === 'string' && body.title) return body.title;
      if (typeof body?.message === 'string' && body.message) return body.message;
      const firstErrorMessage = body?.errors?.errors?.[0]?.message ?? body?.errors?.[0]?.message;
      if (typeof firstErrorMessage === 'string' && firstErrorMessage) return firstErrorMessage;
    } catch {
      // Body wasn't JSON (or was already consumed) -- fall through to the status text below.
    }
    return `API Error: ${response.status} ${response.statusText}`;
  }

  /**
   * Generic POST request
   */
  private async post<T>(path: string, body: unknown): Promise<T> {
    const response = await fetch(`${this.baseUrl}${path}`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      if (response.status === 409) {
        throw new Error('CONFLICT: Template execution already in progress');
      }
      throw new Error(await this.extractErrorMessage(response));
    }

    const data = await response.json();

    // Handle different response structures
    if (data.data !== undefined) {
      return data.data as T;
    }
    // Handle InsertWebResponse structure (success flag, no data wrapper)
    if (data.success === false && data.errors) {
      throw new Error(data.message || 'API request failed');
    }
    // If response has success flag and data is present, return full response as-is
    // (it may contain nested ID structure from InsertWebResponse)
    return data as T;
  }

  /**
   * Generic PUT request
   */
  private async put<T>(path: string, body: unknown): Promise<T> {
    const response = await fetch(`${this.baseUrl}${path}`, {
      method: 'PUT',
      headers: this.getHeaders(),
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      throw new Error(await this.extractErrorMessage(response));
    }

    const data = await response.json();
    if (data.data !== undefined) {
      return data.data as T;
    }
    return data as T;
  }

  /**
   * Generic DELETE request
   */
  private async delete<T>(path: string, body?: unknown): Promise<T> {
    const response = await fetch(`${this.baseUrl}${path}`, {
      method: 'DELETE',
      headers: this.getHeaders(),
      body: body ? JSON.stringify(body) : undefined,
    });

    if (!response.ok) {
      throw new Error(await this.extractErrorMessage(response));
    }

    const data = await response.json();
    return data as T;
  }

  // ============= EXECUTION TEMPLATE ENDPOINTS =============

  async createExecutionTemplate(request: CreateExecutionTemplateRequest): Promise<ExecutionTemplate> {
    return this.post<ExecutionTemplate>('/api/v1/process/execution-templates', { data: request });
  }

  async getExecutionTemplate(id: number): Promise<ExecutionTemplate> {
    return this.post<ExecutionTemplate>(`/api/v1/process/execution-templates/by-id`, { ID: id });
  }

  async searchExecutionTemplates(query: Record<string, unknown>): Promise<ExecutionTemplate[]> {
    const response = await this.post<ExecutionTemplate[]>('/api/v1/process/execution-templates/list', query);
    return Array.isArray(response) ? response : [];
  }

  async updateExecutionTemplate(id: number, updates: Partial<UpdateExecutionTemplateRequest>): Promise<ExecutionTemplate> {
    const request = { executionTemplateID: id, ...updates };
    return this.put<ExecutionTemplate>(
      `/api/v1/process/execution-templates/${id}`,
      { data: request }
    );
  }

  async deleteExecutionTemplate(id: number): Promise<void> {
    await this.delete<void>(`/api/v1/process/execution-templates/${id}`, { ID: id });
  }

  async getExecutionTemplatesByScope(
    scope: 'Process' | 'ProcessThread' | 'ProcessElement',
    scopeRefId: number
  ): Promise<ExecutionTemplate[]> {
    const response = await this.post<any[]>('/api/v1/process/execution-templates/by-scope-reference', {
      executionScope: scope,
      scopeReferenceID: scopeRefId,
    });
    return Array.isArray(response) ? response : [];
  }

  async getExecutionTemplatesBySlug(slug: string): Promise<ExecutionTemplate[]> {
    const response = await this.post<any[]>('/api/v1/process/execution-templates/by-slug', {
      slug: slug,
    });
    return Array.isArray(response) ? response : [];
  }

  async getActiveExecutionTemplates(query: Record<string, unknown>): Promise<ExecutionTemplate[]> {
    const response = await this.post<any[]>('/api/v1/process/execution-templates/active', query);
    return Array.isArray(response) ? response : [];
  }

  async getEnabledExecutionTemplates(query: Record<string, unknown>): Promise<ExecutionTemplate[]> {
    const response = await this.post<any[]>('/api/v1/process/execution-templates/enabled', query);
    return Array.isArray(response) ? response : [];
  }

  // ============= EXECUTION TEMPLATE TYPE ENDPOINTS (lookup) =============

  async getEnabledExecutionTemplateTypes(): Promise<ExecutionTemplateType[]> {
    const response = await this.post<any[]>('/api/v1/process/execution-template-types/enabled', {
      pageNumber: 1,
      pageSize: 200,
    });
    return Array.isArray(response) ? response : [];
  }

  // ============= EXECUTION TEMPLATE CATEGORY ENDPOINTS (lookup) =============

  async getEnabledExecutionTemplateCategories(): Promise<ExecutionTemplateCategory[]> {
    const response = await this.post<any[]>('/api/v1/process/execution-template-categories/enabled', {
      pageNumber: 1,
      pageSize: 200,
    });
    return Array.isArray(response) ? response : [];
  }

  // ============= EXECUTION PIPELINE ENDPOINTS =============

  async createExecutionPipeline(request: CreateExecutionPipelineRequest): Promise<ExecutionPipeline> {
    return this.post<ExecutionPipeline>('/api/v1/process/execution-pipelines', { data: request });
  }

  async getExecutionPipeline(id: number): Promise<ExecutionPipeline> {
    return this.post<ExecutionPipeline>(`/api/v1/process/execution-pipelines/by-id`, { ID: id });
  }

  async searchExecutionPipelines(query: Record<string, unknown>): Promise<ExecutionPipeline[]> {
    const response = await this.post<ExecutionPipeline[]>('/api/v1/process/execution-pipelines/list', query);
    return Array.isArray(response) ? response : [];
  }

  async updateExecutionPipeline(id: number, updates: Partial<UpdateExecutionPipelineRequest>): Promise<ExecutionPipeline> {
    const request = { executionPipelineID: id, ...updates };
    return this.put<ExecutionPipeline>(
      `/api/v1/process/execution-pipelines/${id}`,
      { data: request }
    );
  }

  async deleteExecutionPipeline(id: number): Promise<void> {
    await this.delete<void>(`/api/v1/process/execution-pipelines/${id}`, { ID: id });
  }

  async getEnabledExecutionPipelines(query: Record<string, unknown>): Promise<ExecutionPipeline[]> {
    const response = await this.post<any[]>('/api/v1/process/execution-pipelines/enabled', query);
    return Array.isArray(response) ? response : [];
  }

  // ============= EXECUTION PIPELINE ITEM ENDPOINTS =============

  async createExecutionPipelineItem(request: CreateExecutionPipelineItemRequest): Promise<ExecutionPipelineItem> {
    return this.post<ExecutionPipelineItem>('/api/v1/process/execution-pipeline-items', { data: request });
  }

  async getExecutionPipelineItem(id: number): Promise<ExecutionPipelineItem> {
    return this.post<ExecutionPipelineItem>(`/api/v1/process/execution-pipeline-items/by-id`, { ID: id });
  }

  async updateExecutionPipelineItem(id: number, updates: Partial<UpdateExecutionPipelineItemRequest>): Promise<ExecutionPipelineItem> {
    const request = { executionPipelineItemID: id, ...updates };
    return this.put<ExecutionPipelineItem>(
      `/api/v1/process/execution-pipeline-items/${id}`,
      { data: request }
    );
  }

  async deleteExecutionPipelineItem(id: number): Promise<void> {
    await this.delete<void>(`/api/v1/process/execution-pipeline-items/${id}`, { ID: id });
  }

  async getExecutionPipelineItems(pipelineId: number): Promise<ExecutionPipelineItem[]> {
    const response = await this.post<any[]>('/api/v1/process/execution-pipeline-items/by-pipeline', {
      executionPipelineID: pipelineId,
    });
    return Array.isArray(response) ? response : [];
  }

  async getExecutionPipelineItemsByTemplate(templateId: number): Promise<ExecutionPipelineItem[]> {
    const response = await this.post<any[]>('/api/v1/process/execution-pipeline-items/by-template', {
      executionTemplateID: templateId,
    });
    return Array.isArray(response) ? response : [];
  }

  async getEnabledExecutionPipelineItems(query: Record<string, unknown>): Promise<ExecutionPipelineItem[]> {
    const response = await this.post<any[]>('/api/v1/process/execution-pipeline-items/enabled', query);
    return Array.isArray(response) ? response : [];
  }

  async reorderExecutionPipelineItems(
    pipelineId: number,
    orderedItemIds: number[]
  ): Promise<void> {
    await this.post<void>('/api/v1/process/execution-pipeline-items/reorder', {
      executionPipelineID: pipelineId,
      orderedItemIDs: orderedItemIds,
    });
  }

  async refreshExecutionPipelineSnapshot(pipelineItemId: number): Promise<ExecutionPipelineItem> {
    return this.post<ExecutionPipelineItem>(`/api/v1/process/execution-pipeline-items/${pipelineItemId}/refresh-snapshot`, {
      ID: pipelineItemId,
    });
  }

  // ============= EXECUTION PIPELINE RUN ENDPOINTS =============

  async getExecutionPipelineRun(id: number): Promise<ExecutionPipelineRun> {
    return this.post<ExecutionPipelineRun>(`/api/v1/process/execution-pipeline-runs/by-id`, { ID: id });
  }

  async getExecutionPipelineRuns(query: Record<string, unknown>): Promise<ExecutionPipelineRun[]> {
    const response = await this.post<any[]>('/api/v1/process/execution-pipeline-runs/list', query);
    return Array.isArray(response) ? response : [];
  }

  async getExecutionPipelineRunsByPipeline(pipelineId: number): Promise<ExecutionPipelineRun[]> {
    const response = await this.post<any[]>('/api/v1/process/execution-pipeline-runs/by-pipeline', {
      executionPipelineID: pipelineId,
    });
    return Array.isArray(response) ? response : [];
  }

  async getExecutionPipelineRunsByStatus(status: string): Promise<ExecutionPipelineRun[]> {
    const response = await this.post<any[]>('/api/v1/process/execution-pipeline-runs/by-status', {
      status: status,
    });
    return Array.isArray(response) ? response : [];
  }

  async getActiveExecutionPipelineRuns(query: Record<string, unknown>): Promise<ExecutionPipelineRun[]> {
    const response = await this.post<any[]>('/api/v1/process/execution-pipeline-runs/active', query);
    return Array.isArray(response) ? response : [];
  }

  async getCancellationRequestedRuns(query: Record<string, unknown>): Promise<ExecutionPipelineRun[]> {
    const response = await this.post<any[]>('/api/v1/process/execution-pipeline-runs/cancellation-requested', query);
    return Array.isArray(response) ? response : [];
  }

  async updateExecutionPipelineRunStatus(pipelineRunId: number, status: string, currentItemIndex?: number, completedAt?: string): Promise<void> {
    await this.post<void>(`/api/v1/process/execution-pipeline-runs/${pipelineRunId}/update-status`, {
      ID: pipelineRunId,
      status: status,
      currentItemIndex: currentItemIndex,
      completedAt: completedAt,
    });
  }

  async requestExecutionPipelineRunCancellation(pipelineRunId: number): Promise<void> {
    await this.post<void>(`/api/v1/process/execution-pipeline-runs/${pipelineRunId}/request-cancellation`, {
      ID: pipelineRunId,
    });
  }

  // ============= EXECUTION PIPELINE RUN ITEM ENDPOINTS =============

  async getExecutionPipelineRunItems(runId: number): Promise<ExecutionPipelineRunItem[]> {
    const response = await this.post<any[]>('/api/v1/process/execution-pipeline-run-items/by-run', {
      pipelineRunID: runId,
    });
    return Array.isArray(response) ? response : [];
  }

  async updateExecutionPipelineRunItemStatus(
    runItemId: number,
    status: string,
    timedOut?: boolean,
    completedAt?: string
  ): Promise<void> {
    await this.post<void>(`/api/v1/process/execution-pipeline-run-items/${runItemId}/update-status`, {
      ID: runItemId,
      status: status,
      timedOut: timedOut,
      completedAt: completedAt,
    });
  }

  // ============= EXECUTION SCHEDULE ENDPOINTS =============

  async createExecutionSchedule(request: any): Promise<ExecutionSchedule> {
    return this.post<ExecutionSchedule>('/api/v1/process/execution-schedules', { data: request });
  }

  async getExecutionSchedule(id: number): Promise<ExecutionSchedule> {
    return this.post<ExecutionSchedule>(`/api/v1/process/execution-schedules/by-id`, { ID: id });
  }

  async getExecutionSchedules(query: Record<string, unknown>): Promise<ExecutionSchedule[]> {
    const response = await this.post<any[]>('/api/v1/process/execution-schedules/list', query);
    return Array.isArray(response) ? response : [];
  }

  async updateExecutionSchedule(id: number, updates: Partial<any>): Promise<ExecutionSchedule> {
    const request = { executionScheduleID: id, ...updates };
    return this.put<ExecutionSchedule>(
      `/api/v1/process/execution-schedules/${id}`,
      { data: request }
    );
  }

  async deleteExecutionSchedule(id: number): Promise<void> {
    await this.delete<void>(`/api/v1/process/execution-schedules/${id}`, { ID: id });
  }

  async getExecutionSchedulesByTarget(targetType: string, targetId: number): Promise<ExecutionSchedule[]> {
    const response = await this.post<any[]>('/api/v1/process/execution-schedules/by-target', {
      scheduleTargetType: targetType,
      scheduleTargetID: targetId,
    });
    return Array.isArray(response) ? response : [];
  }

  async getEnabledExecutionSchedules(query: Record<string, unknown>): Promise<ExecutionSchedule[]> {
    const response = await this.post<any[]>('/api/v1/process/execution-schedules/enabled', query);
    return Array.isArray(response) ? response : [];
  }

  async getActiveExecutionSchedules(query: Record<string, unknown>): Promise<ExecutionSchedule[]> {
    const response = await this.post<any[]>('/api/v1/process/execution-schedules/active', query);
    return Array.isArray(response) ? response : [];
  }

  async getDueExecutionSchedules(nextExecutionAt: string): Promise<ExecutionSchedule[]> {
    const response = await this.post<any[]>('/api/v1/process/execution-schedules/due', {
      nextExecutionAt: nextExecutionAt,
    });
    return Array.isArray(response) ? response : [];
  }

  async getExecutionSchedulesByNextExecution(query: Record<string, unknown>): Promise<ExecutionSchedule[]> {
    const response = await this.post<any[]>('/api/v1/process/execution-schedules/next-execution', query);
    return Array.isArray(response) ? response : [];
  }

  async refreshExecutionScheduleSnapshot(scheduleId: number): Promise<ExecutionSchedule> {
    return this.post<ExecutionSchedule>(`/api/v1/process/execution-schedules/${scheduleId}/refresh-snapshot`, {
      ID: scheduleId,
    });
  }

  async recordExecutionScheduleTriggered(scheduleId: number): Promise<void> {
    await this.post<void>(`/api/v1/process/execution-schedules/${scheduleId}/record-triggered`, {});
  }

  // ============= EXECUTION ENDPOINTS (ProcessEngine Layer) =============

  async executeExecutionTemplate(id: number): Promise<{ executionID: string }> {
    return this.post<{ executionID: string }>(`/api/v1/process-engine/execution/execution-templates/${id}/execute`, {});
  }

  async executeExecutionPipeline(id: number): Promise<{ pipelineRunID: number }> {
    return this.post<{ pipelineRunID: number }>(`/api/v1/process-engine/execution/execution-pipelines/${id}/execute`, {});
  }

  async cancelExecutionPipelineRun(pipelineRunId: number): Promise<void> {
    await this.post<void>(`/api/v1/process-engine/execution/execution-pipeline-runs/${pipelineRunId}/cancel`, {
      pipelineRunID: pipelineRunId,
    });
  }

  /**
   * Chat Now's AppID resolution — same two existing, already-authorized endpoints
   * `@bizfirst/ai-agent-catalog-ui`'s `AiAgentCatalogApiClient.resolveChatTarget` uses
   * (`BaseProcessThreadController`'s `by-id` action to resolve a ProcessThread's parent ProcessID,
   * then `BaseAppProcessController`'s `by-process` action to find the App linked to it). No new
   * backend endpoint — reused logic, not a second implementation of the resolution itself, kept as
   * a small local method here rather than pulling the whole `ai-agent-catalog-ui` package in as a
   * dependency for one function (this app doesn't otherwise depend on it, doesn't reuse
   * `TemplateCard`/`HilPresentationPanel`, and has its own `ExecutionTemplate` type/client shape).
   */
  async resolveChatTarget(
    executionScope: 'Process' | 'ProcessThread' | 'ProcessElement',
    scopeReferenceID: number
  ): Promise<{ appID: number; processID: number }> {
    let processID: number;

    if (executionScope === 'ProcessThread') {
      const thread = await this.post<{ processID: number }>('/api/v1/process/process-threads/by-id', {
        ID: scopeReferenceID,
      });
      if (!thread?.processID) {
        throw new Error(`ProcessThread ${scopeReferenceID} was not found.`);
      }
      processID = thread.processID;
    } else if (executionScope === 'Process') {
      processID = scopeReferenceID;
    } else {
      throw new Error(`Chat Now is not supported for ExecutionScope "${executionScope}" (only Process and ProcessThread resolve to a ChatDesk conversation).`);
    }

    const appProcesses = await this.post<Array<{ appID: number; processID: number; isDefault: boolean }>>(
      '/api/v1/ai-extension/app-processes/by-process',
      { processID: { ID: processID } }
    );
    const rows = Array.isArray(appProcesses) ? appProcesses : [];
    if (rows.length === 0) {
      throw new Error(`No app is linked to ProcessID ${processID} (AIExt_AppProcesses has no matching row).`);
    }
    const chosen = rows.find((r) => r.isDefault) ?? rows[0];
    return { appID: chosen.appID, processID };
  }
}

/**
 * Singleton instance (initialize once at app start)
 */
let clientInstance: ExecutionPipelinesApiClient | null = null;

export function initializeApiClient(config: ClientConfig): void {
  clientInstance = new ExecutionPipelinesApiClient(config);
}

export function getApiClient(): ExecutionPipelinesApiClient {
  if (!clientInstance) {
    throw new Error('API Client not initialized. Call initializeApiClient() first.');
  }
  return clientInstance;
}
